"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
type ScrollIntent = "pinned" | "reading" | "auto";
const BOTTOM_THRESHOLD_PX = 50;
const SCROLL_INTENT_DELTA_PX = 20;
const getScrollPosition = (container: HTMLDivElement) => {
  const {
    scrollTop,
    scrollHeight,
    clientHeight
  } = container;
  const distanceFromBottom = Math.abs(scrollTop);
  const isAtBottom = distanceFromBottom < BOTTOM_THRESHOLD_PX;
  const maxNegativeScroll = -(scrollHeight - clientHeight);
  const isAtTop = Math.abs(scrollTop - maxNegativeScroll) < BOTTOM_THRESHOLD_PX;
  return {
    scrollTop,
    isAtBottom,
    isAtTop
  };
};
interface BottomAnchorObserverOptions {
  readonly scrollContainerEl: HTMLDivElement | null;
  readonly bottomAnchorEl: HTMLDivElement | null;
  readonly setIsAtBottom: React.Dispatch<React.SetStateAction<boolean>>;
  readonly setScrollIntentSafely: (nextIntent: ScrollIntent) => void;
}
const useBottomAnchorObserver = t0 => {
  const $ = _c(6);
  const {
    scrollContainerEl,
    bottomAnchorEl,
    setIsAtBottom,
    setScrollIntentSafely
  } = t0;
  let t1;
  let t2;
  if ($[0] !== bottomAnchorEl || $[1] !== scrollContainerEl || $[2] !== setIsAtBottom || $[3] !== setScrollIntentSafely) {
    t1 = () => {
      const container = scrollContainerEl;
      const anchor = bottomAnchorEl;
      if (!container || !anchor) {
        return;
      }
      if (typeof IntersectionObserver === "undefined") {
        return;
      }
      const observer = new IntersectionObserver(t3 => {
        const [entry] = t3;
        const isIntersecting = Boolean(entry?.isIntersecting);
        setIsAtBottom(isIntersecting);
        if (isIntersecting) {
          setScrollIntentSafely("pinned");
        }
      }, {
        root: container,
        threshold: 0,
        rootMargin: "50px"
      });
      observer.observe(anchor);
      return () => {
        observer.disconnect();
      };
    };
    t2 = [scrollContainerEl, bottomAnchorEl, setIsAtBottom, setScrollIntentSafely];
    $[0] = bottomAnchorEl;
    $[1] = scrollContainerEl;
    $[2] = setIsAtBottom;
    $[3] = setScrollIntentSafely;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
};
interface ScrollListenerOptions {
  readonly scrollContainerEl: HTMLDivElement | null;
  readonly handleScroll: () => void;
}
const useScrollListener = t0 => {
  const $ = _c(4);
  const {
    scrollContainerEl,
    handleScroll
  } = t0;
  let t1;
  let t2;
  if ($[0] !== handleScroll || $[1] !== scrollContainerEl) {
    t1 = () => {
      const container = scrollContainerEl;
      if (!container) {
        return;
      }
      container.addEventListener("scroll", handleScroll);
      const initialScrollTimer = setTimeout(() => {
        handleScroll();
      }, 0);
      return () => {
        clearTimeout(initialScrollTimer);
        container.removeEventListener("scroll", handleScroll);
      };
    };
    t2 = [scrollContainerEl, handleScroll];
    $[0] = handleScroll;
    $[1] = scrollContainerEl;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
};
const useScrollContainer = () => {
  const $ = _c(3);
  const scrollContainerRef = useRef(null);
  const [scrollContainerEl, setScrollContainerEl] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = node => {
      if (scrollContainerRef.current === node) {
        return;
      }
      scrollContainerRef.current = node;
      setScrollContainerEl(node);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const syncScrollContainerRef = t0;
  let t1;
  if ($[1] !== scrollContainerEl) {
    t1 = {
      scrollContainerRef,
      scrollContainerEl,
      syncScrollContainerRef
    };
    $[1] = scrollContainerEl;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const useBottomAnchor = () => {
  const $ = _c(3);
  const bottomAnchorRef = useRef(null);
  const [bottomAnchorEl, setBottomAnchorEl] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = node => {
      if (bottomAnchorRef.current === node) {
        return;
      }
      bottomAnchorRef.current = node;
      setBottomAnchorEl(node);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const syncBottomAnchorRef = t0;
  let t1;
  if ($[1] !== bottomAnchorEl) {
    t1 = {
      bottomAnchorRef,
      bottomAnchorEl,
      syncBottomAnchorRef
    };
    $[1] = bottomAnchorEl;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const useScrollIntentState = () => {
  const $ = _c(3);
  const [scrollIntent, setScrollIntent] = useState("pinned");
  const scrollIntentRef = useRef("pinned");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = nextIntent => {
      if (scrollIntentRef.current === nextIntent) {
        return;
      }
      scrollIntentRef.current = nextIntent;
      setScrollIntent(nextIntent);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const setScrollIntentSafely = t0;
  let t1;
  if ($[1] !== scrollIntent) {
    t1 = {
      scrollIntent,
      setScrollIntentSafely
    };
    $[1] = scrollIntent;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
export const useScrollBehavior = () => {
  const {
    scrollContainerRef,
    scrollContainerEl,
    syncScrollContainerRef
  } = useScrollContainer();
  const {
    bottomAnchorRef,
    bottomAnchorEl,
    syncBottomAnchorRef
  } = useBottomAnchor();
  const {
    scrollIntent,
    setScrollIntentSafely
  } = useScrollIntentState();
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [isAtTop, setIsAtTop] = useState(false);
  const lastScrollTopRef = useRef(0);

  // scrollToVisualBottom scrolls to the newest messages (visually at the bottom)
  const forcePinToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    // For a flex-col-reverse container, new messages (bottom) are at scrollTop = 0
    container.scrollTo({
      top: 0,
      behavior
    });

    // Reset to pinned when user manually scrolls to bottom
    setScrollIntentSafely("pinned");
    setIsAtBottom(true);
    lastScrollTopRef.current = 0;
  }, [scrollContainerRef, setScrollIntentSafely]);
  const scrollToVisualBottom = useCallback(() => {
    forcePinToBottom("smooth");
  }, [forcePinToBottom]);

  // scrollToVisualTop scrolls to the oldest messages (visually at the top)
  const scrollToVisualTop = useCallback(() => {
    if (scrollContainerRef.current) {
      // For a flex-col-reverse container, old messages (top) need max scrollTop
      const container_0 = scrollContainerRef.current;

      // Calculate maximum scroll position
      const maxScrollPosition = container_0.scrollHeight - container_0.clientHeight;

      // Set scroll position directly
      container_0.scrollTo({
        top: -maxScrollPosition,
        behavior: "smooth"
      });
    }
  }, []);

  // Robust scroll detection with intent-based pinning
  const handleScroll = useCallback(() => {
    const container_1 = scrollContainerRef.current;
    if (!container_1) return;
    const {
      scrollTop,
      isAtBottom: newIsAtBottom,
      isAtTop: newIsAtTop
    } = getScrollPosition(container_1);
    setIsAtBottom(newIsAtBottom);
    setIsAtTop(newIsAtTop);

    // Intent detection - filter out small/accidental scrolls
    const scrollDelta = Math.abs(scrollTop - lastScrollTopRef.current);
    let nextIntent: ScrollIntent | null = null;
    if (scrollDelta > SCROLL_INTENT_DELTA_PX && !newIsAtBottom) {
      // Significant scroll away from bottom = user is reading
      nextIntent = "reading";
    } else if (newIsAtBottom) {
      // User scrolled back to bottom
      nextIntent = "pinned";
    }
    if (nextIntent) {
      setScrollIntentSafely(nextIntent);
    }
    lastScrollTopRef.current = scrollTop;
  }, [scrollContainerRef, setScrollIntentSafely]);
  const recomputeScrollMetrics = useCallback(() => {
    const container_2 = scrollContainerRef.current;
    if (!container_2) return;
    const {
      isAtBottom: newIsAtBottom_0,
      isAtTop: newIsAtTop_0
    } = getScrollPosition(container_2);
    setIsAtBottom(newIsAtBottom_0);
    setIsAtTop(newIsAtTop_0);
  }, [scrollContainerRef]);
  const recomputePinState = useCallback(() => {
    const container_3 = scrollContainerRef.current;
    if (!container_3) return;
    const {
      isAtBottom: newIsAtBottom_1,
      isAtTop: newIsAtTop_1
    } = getScrollPosition(container_3);
    setIsAtBottom(newIsAtBottom_1);
    setIsAtTop(newIsAtTop_1);
  }, [scrollContainerRef]);

  // Intersection Observer for robust bottom detection
  useBottomAnchorObserver({
    scrollContainerEl,
    bottomAnchorEl,
    setIsAtBottom,
    setScrollIntentSafely
  });
  useScrollListener({
    scrollContainerEl,
    handleScroll
  });
  useEffect(() => {
    const container_4 = scrollContainerEl;
    if (!container_4) {
      return;
    }
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      recomputeScrollMetrics();
    });
    resizeObserver.observe(container_4);
    return () => {
      resizeObserver.disconnect();
    };
  }, [scrollContainerEl, recomputeScrollMetrics]);

  // Should pin when user intends to stay at bottom AND is actually at bottom
  const shouldPinToBottom = scrollIntent === "pinned" && isAtBottom;
  return {
    scrollContainerRef,
    scrollContainerCallbackRef: syncScrollContainerRef,
    bottomAnchorRef,
    bottomAnchorCallbackRef: syncBottomAnchorRef,
    isAtBottom,
    isAtTop,
    shouldPinToBottom,
    scrollIntent,
    scrollToVisualBottom,
    forcePinToBottom,
    scrollToVisualTop,
    handleScroll,
    recomputePinState
  };
};